import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import translate from "../../../i18n/translate";
import ApiRequest from "../../../actions/ApiRequest";
import Toast from "../../common/Toast";

// Every Azy product. The switches, the type and the titles are the admin's
// and survive a product update; price and allowances come from Azy and are
// read-only. Type decides how a sale is priced and the update cannot read it
// from the site, so a product without one is flagged here.
const AzyProductsTable = ({ isDarktheme }) => {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await ApiRequest.post("azy_products?operation=get");
      if (res?.data?.status === "success") {
        setProducts(res.data.products || []);
        setEdits({});
      }
    } catch (err) {
      // the axios interceptor already reports the failure
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const patch = (row, fields) =>
    setProducts((ps) => ps.map((p) => (p.row === row ? { ...p, ...fields } : p)));

  const edit = (row, field, value) =>
    setEdits((e) => ({ ...e, [row]: { ...(e[row] || {}), [field]: value } }));

  const pendingTitles = (p) => {
    const e = edits[p.row] || {};
    const body = {};
    ["en_title", "ar_title", "he_title"].forEach((f) => {
      if (e[f] !== undefined && e[f].trim() !== p[f]) body[f] = e[f].trim();
    });
    return body;
  };

  const save = async (p, extra = {}) => {
    const body = { row: p.row, ...pendingTitles(p), ...extra };
    if (Object.keys(body).length === 1) return;
    setBusy(p.row);
    try {
      const res = await ApiRequest.post("azy_products?operation=update", body);
      if (res?.data?.status === "success") {
        patch(p.row, body);
        setEdits((e) => ({ ...e, [p.row]: {} }));
      } else {
        Toast.fire({ title: res?.data?.reason || "Something Went Wrong!", icon: "error" });
      }
    } catch (err) {
      Toast.fire({ title: "Something Went Wrong!", icon: "error" });
    } finally {
      setBusy(null);
    }
  };

  const switchAll = async (active) => {
    setBusy("all");
    try {
      const res = await ApiRequest.post("azy_products?operation=all", { active });
      if (res?.data?.status === "success") {
        setProducts((ps) => ps.map((p) => ({ ...p, active })));
      } else {
        Toast.fire({ title: res?.data?.reason || "Something Went Wrong!", icon: "error" });
      }
    } catch (err) {
      Toast.fire({ title: "Something Went Wrong!", icon: "error" });
    } finally {
      setBusy(null);
    }
  };

  const titleField = (p, field, label) => (
    <TextField
      size="small"
      fullWidth
      label={label}
      value={(edits[p.row] || {})[field] !== undefined ? edits[p.row][field] : p[field]}
      onChange={(ev) => edit(p.row, field, ev.target.value)}
      inputProps={{ style: { fontSize: 13 } }}
    />
  );

  if (loading && products.length === 0) {
    return (
      <Box sx={{ padding: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const enabled = products.filter((p) => p.active).length;
  const untyped = products.filter((p) => !p.type).length;

  return (
    <Box sx={{ padding: 3, color: isDarktheme ? "#fff" : "inherit" }}>
      <Typography variant="h6" sx={{ marginBottom: 0.5 }}>
        {translate("Products")} · {products.length}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.75, marginBottom: 2, maxWidth: "70ch" }}>
        {translate("azyProductsTableHint")}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2, flexWrap: "wrap" }}>
        <Chip
          size="small"
          color={enabled === products.length ? "success" : enabled === 0 ? "default" : "warning"}
          label={<>{enabled} / {products.length} {translate("enabled")}</>}
        />
        <Button size="small" disabled={busy === "all"} onClick={() => switchAll(true)}>
          {translate("Enable all")}
        </Button>
        <Button size="small" color="inherit" disabled={busy === "all"} onClick={() => switchAll(false)}>
          {translate("Disable all")}
        </Button>
      </Box>

      {untyped > 0 && (
        <Alert severity="warning" sx={{ marginBottom: 2, maxWidth: 900 }}>
          {untyped} {translate("azyUntypedWarning")}
        </Alert>
      )}

      <Paper sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{translate("Titles")}</TableCell>
              <TableCell align="right">₪</TableCell>
              <TableCell>{translate("Type")}</TableCell>
              <TableCell>{translate("Allowances")}</TableCell>
              <TableCell>{translate("Code")}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((p) => {
              const dirty = Object.keys(pendingTitles(p)).length > 0;
              return (
                <TableRow key={p.row} hover sx={{ opacity: p.active ? 1 : 0.55 }}>
                  <TableCell sx={{ width: 60 }}>
                    <Switch
                      size="small"
                      checked={p.active}
                      disabled={busy === p.row}
                      onChange={(ev) => save(p, { active: ev.target.checked })}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 320 }}>
                    <Box sx={{ display: "grid", gap: 1 }}>
                      {titleField(p, "en_title", "EN")}
                      {titleField(p, "ar_title", "AR")}
                      {titleField(p, "he_title", "HE")}
                    </Box>
                    {p.en_des && (
                      <Typography variant="caption" sx={{ opacity: 0.6 }}>
                        {p.en_des}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                    {p.amount}
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      displayEmpty
                      value={p.type || ""}
                      disabled={busy === p.row}
                      onChange={(ev) => save(p, { type: ev.target.value })}
                      sx={{ minWidth: 92, fontSize: 13 }}
                    >
                      <MenuItem value="" disabled>
                        {translate("not set")}
                      </MenuItem>
                      <MenuItem value="3G">3G</MenuItem>
                      <MenuItem value="MIN">MIN</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12 }}>
                    {[
                      p.min && p.min !== "0" ? `${p.min} min` : null,
                      p.gb && p.gb !== "0" ? `${p.gb} GB` : null,
                      p.sms && p.sms !== "0" ? `${p.sms} SMS` : null,
                      p.pal_min && p.pal_min !== "0" ? `${p.pal_min} PAL` : null,
                      p.valid && p.valid !== "0" ? p.valid : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12 }}>{p.code}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!dirty || busy === p.row}
                      onClick={() => save(p)}
                    >
                      {busy === p.row ? <CircularProgress size={16} color="inherit" /> : translate("Save")}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

const mapStateToProps = ({ darkTheme: { isDarktheme } }) => ({ isDarktheme });

export default connect(mapStateToProps)(AzyProductsTable);
